import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { CommunityContentAccessService } from '../../../../community-content/community-content-access.service';
import { HdLineCompanionPanelService } from '../../../../community-content/line-companion/hd-line-companion-panel.service';
import { HdChartSettingsStore } from '../../chart-tabs/hd-chart-settings.store';
import { HdIChingAccessService } from '../../../../iching/hd-iching-access.service';
import { HdIChingPanelService } from '../../../../iching/panel/hd-iching-panel.service';
import { IChingLinePosition } from '../../../../iching/core/iching.models';
import { HdGateLineActivationViewModel, HdGateLineViewModel } from './hd-gate-line.view-model';
import {
  HdGateLineDualityEmphasis,
  HdGateLineDualityRowComponent,
} from './hd-gate-line-duality-row.component';
import { HdFixingState } from '../../planet-grid/hd-planet-display-config';
import { HdGateAttributeIconComponent } from '../../gate-attributes/hd-gate-attribute-icon.component';
import { HdOwnLineTextComponent } from '../../../../line-text-review/hd-own-line-text.component';

/**
 * Horizontale lijn-weergave voor de gate-info dialog/docked sectie. Spiegelt de
 * card-layout (groot lijnnummer + bold `gate.line` linksboven, titel ernaast,
 * tekst eronder over de volle breedte, eigenschap-iconen onderaan, oog-toggle
 * rechtsboven voor extra info) maar met meer horizontale ruimte voor brede tekst.
 *
 * Toont daarnaast per lijn de planeet-activaties uit de geladen chart (zwart =
 * personality, rood = design) of een "geen-activatie"-icoon, en kleurt het
 * lijnnummer lichter wanneer de lijn in deze chart niet geactiveerd is.
 *
 * Pure presentational component — de parent bouwt het {@link HdGateLineViewModel}
 * via `buildHdGateLineViewModel`.
 */
@Component({
  selector: 'hd-gate-line',
  templateUrl: './hd-gate-line.component.html',
  styleUrl: './hd-gate-line.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdGateLineDualityRowComponent, HdGateAttributeIconComponent, HdOwnLineTextComponent],
})
export class HdGateLineComponent {
  readonly viewModel = input.required<HdGateLineViewModel>();

  private readonly communityAccess = inject(CommunityContentAccessService);
  private readonly lineCompanionPanel = inject(HdLineCompanionPanelService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Line Companion-icoon achter de keynote (community-content: feature + opt-in). */
  protected readonly lineCompanionVisible = computed(() => this.communityAccess.lineCompanionVisible());

  protected readonly lineCompanionLabel = computed(() => {
    this.contentLang.translationsLoadedTick();
    const model = this.viewModel();
    return this.transloco.translate(
      'humanDesign.chart.lineCompanion.lineTooltip',
      { gate: model.gate, line: model.lineNumber },
      this.contentLang.effectiveLang(),
    );
  });

  protected openLineCompanion(event: MouseEvent): void {
    const model = this.viewModel();
    this.lineCompanionPanel.openGate(model.gate, {
      focusLine: model.lineNumber,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly ichingPanel = inject(HdIChingPanelService);

  /** Classic I Ching-icoon achter de keynote (feature + setting). */
  protected readonly classicIChingVisible = this.ichingAccess.classicVisible;

  protected readonly classicIChingLabel = computed(() => {
    this.contentLang.translationsLoadedTick();
    const model = this.viewModel();
    return this.transloco.translate(
      'humanDesign.chart.classicIChing.lineTooltip',
      { gate: model.gate, line: model.lineNumber },
      this.contentLang.effectiveLang(),
    );
  });

  protected openClassicIChing(event: MouseEvent): void {
    const model = this.viewModel();
    this.ichingPanel.openHexagram(model.gate, {
      focusLine: model.lineNumber as IChingLinePosition,
      origin: { x: event.clientX, y: event.clientY },
    });
  }
  protected readonly isTransitOnlyLine = computed(() => {
    const activations = this.viewModel().activations;
    return activations.length > 0 && activations.every(activation => activation.origin === 'transit');
  });

  /** Toggle voor "extra info" (subText, learningAboutExtended). Default uit. */
  protected readonly showExtra = signal(false);

  private readonly settings = inject(HdChartSettingsStore);

  /**
   * Alternatieve I Tjing-interpretaties (The Book of Lines, Chetan Parkyn) bij de
   * exaltation/detriment tonen — globale setting, default uit. Los van de
   * "extra info"-oog-toggle: staat de setting aan, dan staat de `~`-regel er
   * meteen bij.
   */
  protected readonly showAlternativeIChing = this.settings.alternativeIChingEnabled;

  /**
   * Ra-lijnteksten (blauwe lijnbeschrijving, subtekst/vervolg en de White/Black
   * Book-keynotes) tonen; globale setting, default aan.
   */
  protected readonly showRa = this.settings.raLineTextsEnabled;

  /**
   * Eigen lijnteksten (Mechanics in Motion, ◆ in amber-inkt) tonen; globale
   * setting, default uit. Los te combineren met de Ra- en Chetan-teksten.
   */
  protected readonly showOwn = this.settings.ownLineTextsEnabled;

  /**
   * Heeft deze lijn extra info? Zo niet, dan tonen we de toggle-knop niet. De
   * extra info is Ra-tekst, dus zonder Ra-lijnteksten ook geen knop.
   */
  protected readonly hasExtra = computed<boolean>(() => {
    const model = this.viewModel();
    return this.showRa() && Boolean(model.subText || model.learningAboutExtended);
  });

  protected toggleExtra(): void {
    this.showExtra.update(value => !value);
  }

  /**
   * Fixatie van de activatie waarover de gebruiker hovert (`null` = geen hover).
   * Stuurt tijdelijk de nadruk van de exaltation-/detriment-rijen aan.
   */
  protected readonly hoveredFixing = signal<HdFixingState | null>(null);

  /** Nadruk van de exaltation-rij (hover overschrijft de default-fixatie-staat). */
  protected readonly exaltationState = computed<HdGateLineDualityEmphasis>(() =>
    this.dualityEmphasis('exaltation'),
  );

  /** Nadruk van de detriment-rij (hover overschrijft de default-fixatie-staat). */
  protected readonly detrimentState = computed<HdGateLineDualityEmphasis>(() =>
    this.dualityEmphasis('detriment'),
  );

  protected onActivationHover(activation: HdGateLineActivationViewModel): void {
    this.hoveredFixing.set(this.combineFixingStates(activation.fixing, activation.transitFixing));
  }

  protected onActivationLeave(): void {
    this.hoveredFixing.set(null);
  }

  /**
   * Bepaalt de nadruk van één pool. Bij hover op een activatie met een fixatie
   * (`exaltation`/`detriment`/`both`) volgt de nadruk die hover; anders valt het
   * terug op de default-fixatie van de lijn:
   * - beide gefixeerd → beide `fixed` (duidelijk verschillend van "geen fixatie");
   * - enkel deze pool → `fixed`, de andere `dimmed`;
   * - geen enkele fixatie → `unfixed` (chart-context, geen oorzaak — grijs).
   *
   * Het `neutral`-default van de duality-rij (oude gekleurde weergave) is
   * voorbehouden voor de card-variant zonder chart-context.
   */
  private dualityEmphasis(pole: 'exaltation' | 'detriment'): HdGateLineDualityEmphasis {
    const model = this.viewModel();
    const hovered = this.hoveredFixing();

    const exaltationFixed =
      hovered && hovered !== 'none'
        ? hovered === 'exaltation' || hovered === 'both'
        : model.hasExaltationFixing;
    const detrimentFixed =
      hovered && hovered !== 'none'
        ? hovered === 'detriment' || hovered === 'both'
        : model.hasDetrimentFixing;

    if (!exaltationFixed && !detrimentFixed) {
      return 'unfixed';
    }
    const thisFixed = pole === 'exaltation' ? exaltationFixed : detrimentFixed;
    return thisFixed ? 'fixed' : 'dimmed';
  }

  private combineFixingStates(first: HdFixingState, second: HdFixingState): HdFixingState {
    const hasExaltation = first === 'exaltation' || first === 'both'
      || second === 'exaltation' || second === 'both';
    const hasDetriment = first === 'detriment' || first === 'both'
      || second === 'detriment' || second === 'both';
    if (hasExaltation && hasDetriment) return 'both';
    if (hasExaltation) return 'exaltation';
    return hasDetriment ? 'detriment' : 'none';
  }
}
